import { ArrowBack } from "@mui/icons-material";
import { Box, IconButton, Stack, TextField } from "@mui/material";
import { tagsApi } from "api";
import { getTranslation } from "common";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import LabelForm from "components/labels/LabelForm";
import LabelRow from "components/labels/LabelRow";
import { LabelData, LabelDataWithOptionalId } from "components/labels/label_Interface";
import { useSnackbar, useTranslations } from "hooks";
import React, { useCallback, useEffect, useState } from "react";

interface OrganizationLabelsEditorProps {
  organizationId: number;
}

const OrganizationLabelsEditor: React.FC<OrganizationLabelsEditorProps> = ({
  organizationId,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const [labels, setLabels] = useState<LabelData[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedLabel, setSelectedLabel] = useState<number | "new" | null>(null);
  const [labelForm, setLabelForm] = useState<LabelDataWithOptionalId | null>(null);

  const fetchLabels = useCallback(async () => {
    try {
      const { data } = await tagsApi.get(`/${organizationId}/tags`);
      setLabels(
        data.tag.map((tag: { id: number; name: string; color: string }) => ({
          id: tag.id,
          name: tag.name,
          css: tag.color,
        }))
      );
      setSelectedLabel(null);
    } catch {
      setLabels([]);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const addLabel = async () => {
    if (!labelForm) return;

    try {
      const { data } = await tagsApi.post(`/${organizationId}/tags`, {
        name: labelForm.name,
        color: labelForm.css,
      });
      sendSnack({ message: data.responsemessage, type: "success" });
      fetchLabels();
    } catch {
    }
  };

  const editLabel = async () => {
    if (!labelForm?.id) return;

    try {
      const { data } = await tagsApi.put(`/${organizationId}/tags/${labelForm.id}`, {
        name: labelForm.name,
        color: labelForm.css,
      });
      sendSnack({ message: data.responsemessage, type: "success" });
      fetchLabels();
    } catch {
    }
  };

  const deleteLabel = async (labelId: number) => {
    try {
      await tagsApi.delete(`/${organizationId}/tags/${labelId}`);
      fetchLabels();
    } catch {
    }
  };

  if (selectedLabel) {
    const editingLabel =
      selectedLabel === "new"
        ? undefined
        : labels.find((label) => label.id === selectedLabel);

    return (
      <Stack spacing={2}>
        <IconButton
          size="small"
          onClick={() => setSelectedLabel(null)}
          sx={{ alignSelf: "flex-start" }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <LabelForm
          defaultLabel={editingLabel}
          onChange={(label) => setLabelForm(label)}
        />
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <FlowentiButton
            size="small"
            variant="outlined"
            onClick={selectedLabel === "new" ? addLabel : editLabel}
          >
            {getTranslation(
              translations,
              selectedLabel === "new"
                ? "generic.create.caption"
                : "generic.button.savechanges"
            )}
          </FlowentiButton>
          {selectedLabel !== "new" && (
            <FlowentiButton
              size="small"
              variant="outlined"
              onClick={() => deleteLabel(selectedLabel)}
            >
              {getTranslation(translations, "generic.delete.caption")}
            </FlowentiButton>
          )}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack id="labels-editor" spacing={2}>
      <TextField
        value={searchValue}
        onKeyDown={(event) => event.stopPropagation()}
        onChange={({ target }) => setSearchValue(target.value)}
        label={getTranslation(translations, "generic.searchlabels.caption")}
        variant="outlined"
        size="small"
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {labels
          .filter((label) =>
            label.name.toLowerCase().includes(searchValue.toLowerCase())
          )
          .map((label) => (
            <Stack
              key={label.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <LabelRow
                color={label.css}
                name={label.name}
                onEditClick={() => setSelectedLabel(label.id)}
              />
            </Stack>
          ))}
      </Box>
      <FlowentiButton
        id="create-organization-label"
        size="small"
        variant="outlined"
        onClick={() => setSelectedLabel("new")}
      >
        {getTranslation(translations, "generic.createnewlabel.caption")}
      </FlowentiButton>
    </Stack>
  );
};

export default OrganizationLabelsEditor;
